/**
 * Hook for dispatching canvas actions to the agent.
 *
 * Tasks: T054 (hook creation), T055 (sendCanvasAction via WebSocket RPC)
 */

import { useCallback, useState } from 'react';
import { useWebSocket } from '../context';

// ============================================================================
// Types
// ============================================================================

export interface CanvasAction {
  name: string;
  params?: Record<string, unknown>;
}

export interface CanvasActionResult {
  success: boolean;
  data?: unknown;
  error?: string;
}

export interface ActionState {
  loading: boolean;
  error: string | null;
}

export interface UseCanvasActionsOptions {
  /** Canvas ID for action context */
  canvasId: string;
  /** Artifact ID for action context */
  artifactId: string;
  /** Callback when action completes successfully */
  onSuccess?: (action: CanvasAction, result: unknown) => void;
  /** Callback when action fails */
  onError?: (action: CanvasAction, error: string) => void;
}

export interface UseCanvasActionsReturn {
  /** Send a canvas action to the agent */
  sendAction: (action: CanvasAction) => Promise<CanvasActionResult>;
  /** Current action state (loading, error) */
  actionState: ActionState;
  /** Clear the current error */
  clearError: () => void;
  /** Whether an action is currently in progress */
  isLoading: boolean;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for sending canvas actions to the agent via WebSocket RPC.
 *
 * The action is sent as a JSON-RPC request over the WebSocket connection.
 * The agent processes the action and may respond with updated canvas content.
 */
export function useCanvasActions(options: UseCanvasActionsOptions): UseCanvasActionsReturn {
  const { canvasId, artifactId, onSuccess, onError } = options;

  const wsContext = useWebSocket();
  const [actionState, setActionState] = useState<ActionState>({
    loading: false,
    error: null,
  });

  const sendAction = useCallback(
    async (action: CanvasAction): Promise<CanvasActionResult> => {
      if (!wsContext?.sendRpcRequest) {
        const error = 'WebSocket not connected';
        setActionState({ loading: false, error });
        onError?.(action, error);
        return { success: false, error };
      }

      setActionState({ loading: true, error: null });

      try {
        const result = await wsContext.sendRpcRequest(
          'canvas_action',
          {
            canvasId,
            artifactId,
            action,
          },
          30000, // 30 second timeout
        ) as CanvasActionResult;

        // Normalize result
        const normalizedResult: CanvasActionResult = {
          success: result?.success ?? true,
          data: result?.data ?? result,
          error: result?.error,
        };

        setActionState({ loading: false, error: normalizedResult.error || null });

        if (normalizedResult.success) {
          onSuccess?.(action, normalizedResult.data);
        } else if (normalizedResult.error) {
          onError?.(action, normalizedResult.error);
        }

        return normalizedResult;
      } catch (err) {
        const error = err instanceof Error ? err.message : 'Action failed';
        setActionState({ loading: false, error });
        onError?.(action, error);
        return { success: false, error };
      }
    },
    [wsContext, canvasId, artifactId, onSuccess, onError]
  );

  const clearError = useCallback(() => {
    setActionState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    sendAction,
    actionState,
    clearError,
    isLoading: actionState.loading,
  };
}

/**
 * Simple action handler for use with CanvasRenderer's onAction prop.
 *
 * Wraps useCanvasActions to provide a simplified interface for components.
 */
export function useCanvasActionHandler(
  canvasId: string,
  artifactId: string
): (action: CanvasAction) => Promise<void> {
  const { sendAction } = useCanvasActions({ canvasId, artifactId });

  return useCallback(
    async (action: CanvasAction) => {
      await sendAction(action);
    },
    [sendAction]
  );
}
